import { LoadingState } from "~/components/screens/StateScreen"

import { Capacitor } from "@capacitor/core"
import { SplashScreen } from "@capacitor/splash-screen"

import { type ReactNode, useEffect, useRef, useState } from "react"
import { getApiBaseUrl } from "~/lib/client-url"

const channel = import.meta.env.VITE_LIVE_UPDATE_CHANNEL ?? "production"

const logUpdateEvent = (event: Record<string, unknown>) => {
	const body = JSON.stringify(event)
	const url = new URL("/api/live-updates/events", getApiBaseUrl()).toString()

	if (navigator.sendBeacon) {
		navigator.sendBeacon(url, new Blob([body], { type: "application/json" }))
		return
	}

	void fetch(url, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body,
		keepalive: true
	}).catch(() => undefined)
}

export function UpdateGate({ children }: { children: ReactNode }) {
	const started = useRef(false)
	const [state, setState] = useState<
		{ status: "checking" } | { status: "ready" } | { status: "installing" }
	>({ status: "checking" })

	useEffect(() => {
		if (!Capacitor.isNativePlatform() || started.current) return
		started.current = true

		const checkForUpdate = async () => {
			try {
				const { LiveUpdate } = await import("@capawesome/capacitor-live-update")
				const [ready, currentBundle, versionName, versionCode, device] =
					await Promise.all([
						LiveUpdate.ready().catch(() => null),
						LiveUpdate.getCurrentBundle().catch(() => ({ bundleId: null })),
						LiveUpdate.getVersionName().catch(() => ({ versionName: null })),
						LiveUpdate.getVersionCode().catch(() => ({ versionCode: null })),
						LiveUpdate.getDeviceId().catch(() => ({ deviceId: null }))
					])
				const context = {
					platform: Capacitor.getPlatform(),
					channel,
					currentBundleId: currentBundle.bundleId,
					nativeVersion: versionName.versionName,
					nativeBuild: versionCode.versionCode,
					deviceId: device.deviceId
				}

				if (ready) {
					logUpdateEvent({
						event: ready.rollback ? "rollback" : "ready",
						...context,
						bundleId: ready.currentBundleId,
						metadata: {
							previousBundleId: ready.previousBundleId,
							rollback: ready.rollback
						}
					})
				}

				const manifest = new URL("/api/live-updates/manifest", getApiBaseUrl())
				manifest.searchParams.set("platform", context.platform)
				manifest.searchParams.set("channel", channel)
				if (context.currentBundleId) {
					manifest.searchParams.set("currentBundleId", context.currentBundleId)
				}
				if (context.nativeVersion) {
					manifest.searchParams.set("nativeVersion", context.nativeVersion)
				}
				if (context.nativeBuild) {
					manifest.searchParams.set("nativeBuild", context.nativeBuild)
				}
				if (context.deviceId)
					manifest.searchParams.set("deviceId", context.deviceId)

				const response = await fetch(manifest, { cache: "no-store" })
				if (response.status === 204) {
					setState({ status: "ready" })
					return
				}
				if (!response.ok)
					throw new Error(`Update check failed: ${response.status}`)

				const result = await response.json()
				const update = {
					bundleId: typeof result.bundleId === "string" ? result.bundleId : "",
					url: typeof result.url === "string" ? result.url : "",
					sha256: typeof result.sha256 === "string" ? result.sha256 : "",
					signature:
						typeof result.signature === "string" ? result.signature : undefined
				}

				if (!update.bundleId || !update.url || !update.sha256) {
					throw new Error("Update manifest was invalid")
				}

				setState({ status: "installing" })

				try {
					const downloaded = await LiveUpdate.getDownloadedBundles().catch(
						() => ({
							bundleIds: [] as string[]
						})
					)
					if (!downloaded.bundleIds.includes(update.bundleId)) {
						await LiveUpdate.downloadBundle({
							artifactType: "zip",
							bundleId: update.bundleId,
							url: update.url,
							checksum: update.sha256,
							signature: update.signature
						})
					}
					await LiveUpdate.setNextBundle({ bundleId: update.bundleId })
					logUpdateEvent({
						event: "install_ready",
						...context,
						bundleId: update.bundleId
					})
					await LiveUpdate.reload()
				} catch (error) {
					const message =
						error instanceof Error ? error.message : "Update failed to install"
					logUpdateEvent({
						event: "install_failed",
						...context,
						bundleId: update.bundleId,
						error: message
					})
					console.error("Failed to install native live update", error)
					setState({ status: "ready" })
				}
			} catch (error) {
				console.error("Failed to check native live updates", error)
				setState({ status: "ready" })
			}
		}

		void checkForUpdate()
	}, [])

	useEffect(() => {
		if (!Capacitor.isNativePlatform() || state.status === "checking") return

		void SplashScreen.hide().catch(() => undefined)
	}, [state.status])

	if (!Capacitor.isNativePlatform() || state.status === "ready") return children

	if (state.status === "installing") {
		return <LoadingState scope="page" branded />
	}

	return null
}
